import React, { useState } from 'react';

/**
 * What this alert will actually evaluate, read from the stored query (PRD-1381,
 * PRD-1405).
 *
 * **The largest gap in a conversational design.** The author verifies *output* —
 * three members and their counts — but never sees what was *filtered*. If the
 * model misread "unscheduled" and matched the wrong `absence_type`, the preview
 * still looks entirely plausible and gets approved. The old form made filters
 * explicit fields; a conversation hides them behind a sentence the agent wrote.
 *
 * So this renders **from the definition, not from the conversation**. The
 * agent's own words are what it believed it did; the query is what will run.
 * Rendering the former would reproduce the bug rather than catch it — an agent
 * that misread the request describes it back with the same misreading.
 *
 * For a regulated alerting product, "what exactly will this evaluate" has to be
 * inspectable before arming.
 *
 * ---
 *
 * **This showed prose, and now it shows the query. That is a downgrade in
 * readability and an upgrade in truthfulness, and the second one decides it.**
 *
 * Until PRD-1405 the stored definition was an IR — a small closed grammar — and
 * this walked it into phrases like "absence_type is unscheduled". That reads
 * better than SQL, and it was the right rendering for a document that could only
 * ever hold the handful of shapes the grammar admitted.
 *
 * Storing SQL removes the grammar, and with it the guarantee this renderer
 * silently depended on: that every condition in the document was one it knew how
 * to describe. Applied to arbitrary SQL, a prose walker shows the predicates it
 * recognises and omits the rest — a `CASE`, a cast, a subquery, a `COALESCE`
 * across two columns would each simply not appear. **A conditions panel that
 * quietly drops the conditions it cannot read is worse than no panel**, because
 * it is read as a complete list. That is the same failure this component was
 * written to prevent, arriving through the renderer instead of through the
 * agent.
 *
 * The query itself cannot omit anything. It is longer and it is SQL, so it sits
 * behind the same disclosure rather than in front of the author by default.
 *
 * The window is stated in words beside it, because it is the one part of the
 * query that is *not* in the query: the text carries placeholders, and what they
 * bind to is the `window` document next to it. An author reading
 * `%(window_start)s` learns nothing about the period being measured.
 */

/**
 * The window, as a sentence.
 *
 * Returns null rather than a fallback phrase when there is no window: "no
 * window" and "a window this view cannot read" are different facts, and most
 * queries genuinely have no date filter at all — 11 of 15 measured. Saying
 * nothing here is correct for those, and the caller states the absence in its
 * own words; inventing "all time" would state a rule the query does not contain.
 */
export const describeWindow = (window) => {
  if (!window || typeof window !== 'object') return null;
  const days = Number(window.days);
  const column = String(window.column || '').trim();
  if (!Number.isFinite(days) || days <= 0 || !column) return null;
  const period = days === 1 ? 'the last day' : `the last ${days} days`;
  const table = String(window.table || '').trim();
  return `${period}, by ${table ? `${table}.${column}` : column}`;
};

/** The placeholders a moving window binds at run time. */
const BINDS_A_WINDOW = /%\(window_(?:start|end)\)s/;

/**
 * A date-relative expression the query evaluates itself.
 *
 * `CURRENT_DATE - INTERVAL '30 days'` is a moving window that the extractor did
 * not turn into placeholders — so there is a period, it just is not in the
 * `window` document. Matched loosely on purpose: this decides whether to *stay
 * quiet*, never what the period is, so a false positive costs a sentence and a
 * false negative costs a wrong one.
 */
const MENTIONS_A_PERIOD = /\b(?:CURRENT_DATE|CURRENT_TIMESTAMP|NOW\s*\(|INTERVAL)\b/i;

/**
 * The clock read directly, rather than the run date the service supplies. A
 * word boundary after the names, so a column like `current_date_of_hire` is not
 * the clock (review of this PR); `NOW(` ends in a non-word character already.
 */
const READS_THE_CLOCK = /\b(?:CURRENT_DATE\b|CURRENT_TIMESTAMP\b|NOW\s*\()/i;

/**
 * The run's own date moved by an interval: `%(anchor)s + INTERVAL '30 days'`,
 * optionally cast first. Only an interval applied to the run date counts —
 * `duration > INTERVAL '1 hour'` beside a run-date comparison measures no period
 * (review of this PR). The offset is captured so the panel can state it.
 */
const RUN_DATE_OFFSET = /%\(anchor\)s(?:\s+AS\s+\w+\s*\))?\s*([+-])\s*INTERVAL\s*'\s*(\d+)\s*([a-z]+)\s*'/i;

/** "plus 30 days" from a matched offset, in the words an author reads. */
const describeRunDateOffset = (match) => {
  const [, sign, amount, unit] = match;
  const count = Number(amount);
  const singular = unit.toLowerCase().replace(/s$/, '');
  return `${sign === '-' ? 'minus' : 'plus'} ${count} ${count === 1 ? singular : `${singular}s`}`;
};

// **`window: timeWindow` — the prop name stays, the binding does not.** `window`
// here is a legitimate domain noun, which is exactly what makes shadowing the
// browser global dangerous: someone later adds `window.innerWidth` for a layout
// decision and gets `undefined` rather than a throw, with the error surfacing a
// line later pointing somewhere else. ESLint has no backstop —
// `no-shadow-restricted-names` covers `undefined`/`NaN`/`eval`, not globals.
// Raised in review of #202.
const AlertConditions = ({ sql = null, window: timeWindow = null }) => {
  const [open, setOpen] = useState(false);
  const query = typeof sql === 'string' ? sql.trim() : '';
  if (!query) return null;

  const period = describeWindow(timeWindow);
  // **"No time window" is a claim, and it must not be made about a window this
  // panel merely failed to read.** `describeWindow` returns null for both, by
  // design, and the two are different facts: a query with no date filter really
  // does measure everything, while a query whose `window` went missing on the
  // way here measures a period nobody can see. The query itself settles which —
  // if it binds placeholders there is a window. Telling an author in words that
  // an alert measures all the data, directly above a `<pre>` showing
  // `%(window_start)s`, is the misreading this panel exists to prevent. Raised
  // in review of this branch.
  //
  // **Two ways a window can be present but unreadable, and the second was found
  // live.** The placeholder test catches extraction-succeeded-then-lost. It does
  // not catch extraction never running: a `::date` cast defeated
  // `extract_window`, so a real 30-day filter arrived with no placeholders *and*
  // no window, landing in the branch for "genuinely measures everything" while
  // measuring 30 days. The query said `CURRENT_DATE - INTERVAL '30 days'`
  // directly below the sentence denying it.
  //
  // Fixed at source in micro-services-stack#492; kept here as well because this
  // panel exists to stop an author approving a definition they misread, and it
  // should not depend on the extractor understanding every query. Saying nothing
  // is better than saying the opposite.
  //
  // **A period measured from the run date is not one we failed to read**
  // (PRD-1475). "Licences expiring within the next 30 days, or already expired"
  // compares `expiry_date` with `%(anchor)s + INTERVAL '30 days'`: one-sided,
  // so correctly not a stored window, and bound to the day of each run, so it
  // moves forward on its own. Calling that unreadable told the author to rebuild
  // an alert that was right. The clock read directly, or placeholders whose
  // window was lost, still are.
  //
  // **Every offset, not the first** (review of this PR). `match()` stops at the
  // first, so `d >= %(anchor)s - INTERVAL '7 days' AND d <= %(anchor)s +
  // INTERVAL '30 days'` was described as "minus 7 days" -- a confident claim
  // about a five-week span, in the panel an author uses to check the period.
  // One phrase can state one offset; more than one is a period this panel
  // cannot say in a phrase, and the honest answer is the existing warning.
  const runDateOffsets = !BINDS_A_WINDOW.test(query) && !READS_THE_CLOCK.test(query)
    ? [...query.matchAll(new RegExp(RUN_DATE_OFFSET.source, 'gi'))]
    : [];
  const runDateOffset = new Set(runDateOffsets.map(describeRunDateOffset)).size === 1
    ? runDateOffsets[0]
    : null;
  const hasAWindowWeCannotRead = !runDateOffset
    && (runDateOffsets.length > 0 || BINDS_A_WINDOW.test(query) || MENTIONS_A_PERIOD.test(query));

  return (
    <div className="alert-conditions">
      <button
        type="button"
        className="alert-conditions-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? 'Hide the conditions' : 'Show the conditions'}
      </button>

      {open && (
        <div className="alert-conditions-body" data-testid="alert-conditions-body">
          {period ? (
            <p className="alert-conditions-line">
              <strong>Measures:</strong> {period}
            </p>
          ) : runDateOffset ? (
            <p className="alert-conditions-line">
              <strong>Measures:</strong> dates compared with the day each run
              happens {describeRunDateOffset(runDateOffset)}, so the period moves
              forward on its own.
            </p>
          ) : hasAWindowWeCannotRead ? (
            <p className="alert-conditions-line alert-conditions-none">
              This alert measures a period, but this view could not read which
              one — so it cannot be checked here. Build it again, or use the
              form, rather than saving a period you cannot see.
            </p>
          ) : (
            // Said out loud, for the same reason the empty condition list was.
            // A query with no date filter measures the whole table on every run,
            // which is almost never what an author describing an alert meant and
            // is invisible if left blank.
            <p className="alert-conditions-line alert-conditions-none">
              No time window — this measures all the data, not a recent period.
            </p>
          )}
          {/* **The query itself is deliberately no longer on screen.**

              **Not because the WAF required it**, which is what an earlier
              version of this comment implied and is wrong. The WAF inspects
              request bodies; this is a render. The author response still carries
              `sql` and `useAlertIrAuthor` still holds it — the step validates
              against it — so painting it here sends nothing anywhere, and the
              draft-reference mechanism is complete without this. Raised in
              review of #206.

              It goes on a narrower claim: nothing on the client *needs* the text
              once the panel stops painting it, which is what would let the
              server stop returning it later. That is a step not yet taken, so
              this is a bet on a direction rather than a consequence of one.

              What it costs is real, and is the reason the lines above stay. The
              two incidents in the comment above were both caught by *reading the
              query*. What still catches them is the warning branch below — it
              fires when the query binds placeholders or names a period the view
              could not read, which is exactly what both of those looked like.
              What is gone is the author's ability to check the query against
              their own intent for anything that warning does not cover. */}
          <p className="alert-conditions-note">
            Taken from the definition that will run, not from the conversation.
            {period ? ' The dates are filled in each time it runs.' : ''}
          </p>
        </div>
      )}
    </div>
  );
};

export default AlertConditions;
